import React, { useCallback, useEffect, useRef, useState } from "react";
import { api, formatApiError } from "../../lib/api";
import {
  X, Save, Trash2, ChevronLeft, ChevronRight, Type, AlignLeft,
  Calendar, CheckSquare, PenLine, Signature, Database, MousePointerClick,
} from "lucide-react";

/* Placing fields on an uploaded form.

   The editor is inverted on purpose. A real state form has hundreds of
   printed captions and checkboxes; walking them one by one would mean seven
   hundred boxes before the agent got anything back. So the agent does not
   describe the form -- they pick a thing they want printed (the client's
   name, a date, a tick, a question to answer later) and click where it goes.
   Forty placements, once per form, instead of seven hundred.

   ONE ratio converts between the page and the screen: k = the image's
   displayed width over the page's width in points, read off the element.
   Positions are stored in PDF points with the origin at the bottom-left,
   which is how the PDF measures. Nothing here knows the server's render
   resolution, so changing it later cannot move a single field. */

const muted = { color: "var(--text-muted)" };

/* Default box sizes in POINTS, roughly what each thing needs on a letter page. */
const KIND = {
  text: { label: "Short text", icon: Type, w: 170, h: 14 },
  multiline: { label: "Paragraph", icon: AlignLeft, w: 220, h: 56 },
  date: { label: "Date", icon: Calendar, w: 90, h: 14 },
  check: { label: "Checkbox", icon: CheckSquare, w: 12, h: 12 },
  initial: { label: "Initials", icon: Signature, w: 34, h: 16 },
  signature: { label: "Signature line", icon: PenLine, w: 150, h: 24 },
};

const uid = () =>
  (window.crypto?.randomUUID?.() || `f${Date.now()}${Math.random()}`).replace(/-/g, "").slice(0, 32);

function kindForPath(path) {
  if (path === "client.initials") return "initial";
  return path.startsWith("date.") ? "date" : "text";
}

export default function FormFields({ template, onClose, onSaved }) {
  const tid = template.template_id;

  const [meta, setMeta] = useState(null);
  const [fields, setFields] = useState([]);
  const [prefills, setPrefills] = useState([]);
  const [page, setPage] = useState(0);
  const [armed, setArmed] = useState(null);     // what the next click drops
  const [sel, setSel] = useState("");
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");
  const [imgUrl, setImgUrl] = useState("");
  const [imgErr, setImgErr] = useState("");
  const [k, setK] = useState(0);                // displayed px per PDF point

  const imgRef = useRef(null);
  const cache = useRef({});                     // page -> object URL
  const drag = useRef(null);

  /* Placing a 12-point checkbox means clicking a 7-pixel target on a phone.
     A cramped editor that puts fields in the wrong place is worse than saying
     plainly that this one wants a bigger screen, so below this width we do. */
  const [narrow, setNarrow] = useState(
    typeof window !== "undefined" && window.innerWidth < 900);
  useEffect(() => {
    const onResize = () => setNarrow(window.innerWidth < 900);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  /* Object URLs outlive React state, so they are revoked from one place on
     unmount rather than wherever a page happened to change. */
  useEffect(() => () => {
    Object.values(cache.current).forEach((u) => URL.revokeObjectURL(u));
  }, []);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const [f, p] = await Promise.all([
          api.get(`/admin/form-library/${tid}/fields`),
          api.get("/admin/prefill-fields"),
        ]);
        if (!alive) return;
        setMeta(f.data);
        setFields(f.data.fields || []);
        setPrefills(p.data.fields || []);
      } catch (e) {
        if (alive) setErr(formatApiError(e?.response?.data?.detail) || "Couldn't open that form.");
      }
    })();
    return () => { alive = false; };
  }, [tid]);

  /* The page image needs the auth header, so it cannot be an <img src> to the
     API directly -- it is fetched and handed over as an object URL. */
  useEffect(() => {
    let alive = true;
    setImgErr("");
    // No point pulling a page render down to a screen that is about to be
    // told it's too small for this.
    if (narrow) return;
    if (cache.current[page]) { setImgUrl(cache.current[page]); return; }
    setImgUrl("");
    (async () => {
      try {
        const { data } = await api.get(`/admin/form-library/${tid}/page/${page}.png`,
                                       { responseType: "blob", timeout: 60000 });
        const url = URL.createObjectURL(data);
        if (!alive) { URL.revokeObjectURL(url); return; }
        cache.current[page] = url;
        setImgUrl(url);
      } catch (e) {
        if (alive) {
          setImgErr(formatApiError(e?.response?.data?.detail)
                    || "Couldn't render that page.");
        }
      }
    })();
    return () => { alive = false; };
  }, [tid, page, narrow]);

  const pageSize = (meta?.page_sizes || [])[page];
  const pw = pageSize ? pageSize[0] : 0;
  const ph = pageSize ? pageSize[1] : 0;

  /* k is measured, never assumed, and re-measured whenever the box changes
     width -- a stale k would put every field in the wrong place. */
  const measure = useCallback(() => {
    const el = imgRef.current;
    if (!el || !pw) return;
    const w = el.clientWidth;
    if (w > 0) setK(w / pw);
  }, [pw]);

  useEffect(() => {
    measure();
    const el = imgRef.current;
    if (!el || typeof ResizeObserver === "undefined") {
      window.addEventListener("resize", measure);
      return () => window.removeEventListener("resize", measure);
    }
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [measure, imgUrl]);

  const mine = fields.filter((f) => f.page === page);
  const selected = fields.find((f) => f.field_id === sel) || null;
  // Asking "who fills this in" only means something when more than one
  // party does. On a client-only form the answer is never in doubt.
  const shared = ["both", "agent"].includes(template.completed_by);

  const edit = (id, patch) => {
    setFields((xs) => xs.map((f) => (f.field_id === id ? { ...f, ...patch } : f)));
    setDirty(true);
  };

  const clampRect = (r) => {
    const w = Math.max(6, Math.min(r.w, pw));
    const h = Math.max(6, Math.min(r.h, ph));
    return {
      x: Math.round(Math.max(0, Math.min(r.x, pw - w)) * 100) / 100,
      y: Math.round(Math.max(0, Math.min(r.y, ph - h)) * 100) / 100,
      w: Math.round(w * 100) / 100,
      h: Math.round(h * 100) / 100,
    };
  };

  /* ------------------------------------------------------------ placing */

  const place = (e) => {
    if (!armed || !k || !pageSize) return;
    const box = imgRef.current.getBoundingClientRect();
    const ox = e.clientX - box.left;
    const oy = e.clientY - box.top;
    const spec = KIND[armed.kind];
    // The click is the box's top-left; y is stored as the BOTTOM edge.
    const rect = clampRect({
      x: ox / k,
      y: ph - oy / k - spec.h,
      w: spec.w,
      h: spec.h,
    });
    const f = {
      field_id: uid(),
      page,
      rect,
      kind: armed.kind,
      label: armed.label,
      source: armed.source,
      size: armed.kind === "multiline" ? 9 : 10,
      // On a form only the agent completes, every field is theirs.
      // Anywhere else the client is the safe default, because that is
      // what every field placed before "who" existed already meant.
      who: template.completed_by === "agent" ? "agent" : "seller",
    };
    setFields((xs) => [...xs, f]);
    setSel(f.field_id);
    setArmed(null);
    setDirty(true);
  };

  const startDrag = (e, f, mode) => {
    e.stopPropagation();
    setSel(f.field_id);
    if (!k) return;
    drag.current = { id: f.field_id, mode, x0: e.clientX, y0: e.clientY, rect: f.rect };
    e.currentTarget.setPointerCapture?.(e.pointerId);
  };

  const onMove = (e) => {
    const d = drag.current;
    if (!d || !k) return;
    const dx = (e.clientX - d.x0) / k;
    const dy = (e.clientY - d.y0) / k;
    const r = d.rect;
    // Screen y grows downward, PDF y upward, so a drag down LOWERS y. The
    // resize handle is the bottom-right corner, so dragging it down has to
    // keep the TOP edge (y + h) where it is: y falls by dy and h grows by it.
    const next = d.mode === "move"
      ? { ...r, x: r.x + dx, y: r.y - dy }
      : { ...r, w: r.w + dx, h: r.h + dy, y: r.y - dy };
    edit(d.id, { rect: clampRect(next) });
  };

  const endDrag = () => { drag.current = null; };

  /* Nudging with the keyboard is how a box actually lands on a printed line. */
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") { setArmed(null); return; }
      if (!selected) return;
      const tag = (e.target.tagName || "").toLowerCase();
      if (tag === "input" || tag === "select" || tag === "textarea") return;
      if (e.key === "Delete" || e.key === "Backspace") {
        e.preventDefault();
        setFields((xs) => xs.filter((f) => f.field_id !== selected.field_id));
        setSel("");
        setDirty(true);
        return;
      }
      const step = e.shiftKey ? 10 : 1;
      const by = { ArrowLeft: [-step, 0], ArrowRight: [step, 0],
                   ArrowUp: [0, step], ArrowDown: [0, -step] }[e.key];
      if (!by) return;
      e.preventDefault();
      edit(selected.field_id,
           { rect: clampRect({ ...selected.rect, x: selected.rect.x + by[0], y: selected.rect.y + by[1] }) });
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    /* eslint-disable-next-line */
  }, [selected, pw, ph]);

  const save = async () => {
    setSaving(true);
    setErr("");
    try {
      const { data } = await api.put(`/admin/form-library/${tid}/fields`, { fields });
      setFields(data.fields || fields);
      setDirty(false);
      if (data.dropped) setErr(`${data.dropped} field(s) couldn't be saved and were dropped.`);
      onSaved && onSaved(data.fields || fields);
    } catch (e) {
      setErr(formatApiError(e?.response?.data?.detail) || "Couldn't save those fields.");
    } finally { setSaving(false); }
  };

  const close = () => {
    if (dirty && !window.confirm("Close without saving? The fields you placed will be lost.")) return;
    onClose();
  };

  const pages = meta?.page_count || 0;

  if (narrow) {
    return (
      <div style={{ position: "fixed", inset: 0, zIndex: 900, background: "rgba(28,25,23,0.55)",
                    display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}
           data-testid="form-fields-narrow">
        <div className="card" style={{ background: "#fff", padding: 28, maxWidth: 420 }}>
          <div className="overline">Place fields</div>
          <h2 className="font-serif" style={{ fontSize: 22, fontWeight: 500, margin: "6px 0 10px" }}>
            Open this on a bigger screen
          </h2>
          <p style={{ ...muted, fontSize: 14, marginTop: 0 }}>
            Dropping a box onto a printed line takes a page you can see properly &mdash;
            a laptop or a tablet in landscape. Everything else about{" "}
            <b style={{ color: "var(--text)" }}>{template.name}</b> still works from here.
          </p>
          <button type="button" className="btn-secondary" onClick={onClose}
                  data-testid="ff-narrow-close">
            Back to your forms
          </button>
        </div>
      </div>
    );
  }

  const arm = (spec) => setArmed(armed && armed.key === spec.key ? null : spec);

  const toolBtn = (spec, Icon, sub) => {
    const on = armed?.key === spec.key;
    return (
      <button key={spec.key} type="button" onClick={() => arm(spec)}
              data-testid={`ff-tool-${spec.key}`}
              style={{
                display: "flex", alignItems: "center", gap: 8, width: "100%",
                textAlign: "left", padding: "8px 10px", marginBottom: 4, fontSize: 13,
                borderRadius: 8, cursor: "pointer",
                border: `1px solid ${on ? "var(--primary)" : "var(--border)"}`,
                background: on ? "var(--primary)" : "#fff",
                color: on ? "#fff" : "var(--text)",
              }}>
        <Icon size={14} style={{ flex: "0 0 14px" }} />
        <span style={{ minWidth: 0 }}>
          {spec.label}
          {sub && (
            <span style={{ display: "block", fontSize: 11, opacity: on ? 0.8 : 0.6 }}>{sub}</span>
          )}
        </span>
      </button>
    );
  };

  return (
    <div data-testid="form-fields-editor"
         style={{ position: "fixed", inset: 0, zIndex: 900, background: "rgba(28,25,23,0.55)",
                  display: "flex", justifyContent: "center", alignItems: "stretch", padding: 16 }}
         onMouseDown={(e) => { if (e.target === e.currentTarget) close(); }}>
      <div style={{ background: "var(--surface-alt)", borderRadius: 14, width: "100%", maxWidth: 1180,
                    display: "flex", flexDirection: "column", overflow: "hidden" }}>

        {/* ---------------------------------------------------------- header */}
        <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "14px 18px",
                      borderBottom: "1px solid var(--border)", background: "#fff", flexWrap: "wrap" }}>
          <div style={{ minWidth: 0, flex: "1 1 220px" }}>
            <div className="overline">Place fields</div>
            <div className="font-serif" style={{ fontSize: 19, fontWeight: 500 }}>
              {meta?.name || template.name}
            </div>
          </div>
          {pages > 1 && (
            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <button type="button" className="btn-ghost" style={{ padding: 6 }}
                      disabled={page === 0} data-testid="ff-prev"
                      onClick={() => { setPage((n) => Math.max(0, n - 1)); setSel(""); }}>
                <ChevronLeft size={16} />
              </button>
              <span style={{ fontSize: 13, ...muted, minWidth: 92, textAlign: "center" }}>
                Page {page + 1} of {pages}
              </span>
              <button type="button" className="btn-ghost" style={{ padding: 6 }}
                      disabled={page >= pages - 1} data-testid="ff-next"
                      onClick={() => { setPage((n) => Math.min(pages - 1, n + 1)); setSel(""); }}>
                <ChevronRight size={16} />
              </button>
            </div>
          )}
          <button type="button" className="btn-primary" onClick={save} data-testid="ff-save"
                  disabled={saving || !dirty}
                  style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
            <Save size={14} /> {saving ? "Saving…" : dirty ? "Save fields" : "Saved"}
          </button>
          <button type="button" className="btn-ghost" style={{ padding: 6 }} onClick={close}
                  data-testid="ff-close" title="Close">
            <X size={18} />
          </button>
        </div>

        {err && (
          <div style={{ padding: "8px 18px", background: "#fff", fontSize: 13,
                        color: "var(--secondary)", borderBottom: "1px solid var(--border)" }}>
            {err}
          </div>
        )}

        <div style={{ display: "flex", gap: 0, minHeight: 0, flex: 1 }}>

          {/* -------------------------------------------------------- palette */}
          <div style={{ flex: "0 0 244px", overflowY: "auto", padding: 16,
                        borderRight: "1px solid var(--border)", background: "#fff" }}>
            <div className="overline" style={{ marginBottom: 8 }}>
              <Database size={11} style={{ verticalAlign: -1, marginRight: 4 }} />
              Fill it in for me
            </div>
            {prefills.map((p) => toolBtn(
              { key: `pre:${p.path}`, label: p.label, kind: kindForPath(p.path),
                source: { type: "prefill", path: p.path } },
              KIND[kindForPath(p.path)].icon,
            ))}

            <div className="overline" style={{ margin: "16px 0 8px" }}>
              <MousePointerClick size={11} style={{ verticalAlign: -1, marginRight: 4 }} />
              Ask me each time
            </div>
            {Object.entries(KIND).map(([kind, spec]) => toolBtn(
              { key: `ask:${kind}`, label: spec.label, kind, source: { type: "ask" } },
              spec.icon,
            ))}

            <p style={{ ...muted, fontSize: 12, marginTop: 16, lineHeight: 1.5 }}>
              {armed
                ? <b style={{ color: "var(--primary)" }}>Now click where it goes on the page.</b>
                : "Pick one of these, then click the spot on the form where it belongs."}
            </p>

            {/* ------------------------------------------------- inspector */}
            {selected && (
              <div style={{ borderTop: "1px solid var(--border)", marginTop: 16, paddingTop: 14 }}
                   data-testid="ff-inspector">
                <div className="overline" style={{ marginBottom: 8 }}>Selected field</div>
                <label style={{ fontSize: 12, ...muted }}>What to call it</label>
                <input className="input-field" value={selected.label}
                       data-testid="ff-label"
                       onChange={(e) => edit(selected.field_id, { label: e.target.value })}
                       style={{ width: "100%", padding: "6px 8px", fontSize: 13, marginBottom: 10 }} />

                <label style={{ fontSize: 12, ...muted }}>Kind</label>
                <select className="input-field" value={selected.kind} data-testid="ff-kind"
                        onChange={(e) => edit(selected.field_id, { kind: e.target.value })}
                        style={{ width: "100%", padding: "6px 8px", fontSize: 13, marginBottom: 10 }}>
                  {Object.entries(KIND).map(([kk, s]) => (
                    <option key={kk} value={kk}>{s.label}</option>
                  ))}
                </select>

                {shared && (
                  <>
                    <label style={{ fontSize: 12, ...muted }}>Who fills this in</label>
                    <div style={{ display: "flex", gap: 6, marginTop: 4, marginBottom: 10 }}>
                      {[["seller", "The client"], ["agent", "You"]].map(([v, lab]) => {
                        const picked = (selected.who || "seller") === v;
                        return (
                          <button key={v} type="button" data-testid={`ff-who-${v}`}
                                  onClick={() => edit(selected.field_id, { who: v })}
                                  style={{ flex: 1, padding: "6px 8px", fontSize: 12.5,
                                           borderRadius: 7, cursor: "pointer",
                                           border: "1px solid var(--border)",
                                           background: picked ? "var(--primary)" : "#fff",
                                           color: picked ? "#fff" : "var(--text)" }}>
                            {lab}
                          </button>
                        );
                      })}
                    </div>
                    {selected.who === "agent"
                      && ["signature", "initial"].includes(selected.kind) && (
                      <p data-testid="ff-who-warn"
                         style={{ color: "var(--secondary)", fontSize: 11.5,
                                  marginTop: -4, marginBottom: 10, lineHeight: 1.5 }}>
                        PorchLight only collects the client&rsquo;s signature. A signature
                        or initials box marked as yours stays blank &mdash; sign the PDF
                        yourself after you download it.
                      </p>
                    )}
                  </>
                )}

                <label style={{ fontSize: 12, ...muted }}>Text size</label>
                <input type="number" className="input-field" min={5} max={28} step={0.5}
                       value={selected.size} data-testid="ff-size"
                       onChange={(e) => edit(selected.field_id, { size: Number(e.target.value) || 10 })}
                       style={{ width: "100%", padding: "6px 8px", fontSize: 13, marginBottom: 10 }} />

                <div style={{ ...muted, fontSize: 11.5, marginBottom: 10 }}>
                  {selected.source?.type === "prefill"
                    ? `Filled from ${selected.source.path}`
                    : `${selected.who === "agent" ? "You" : "The client"} will be asked for this`}
                  <br />
                  {Math.round(selected.rect.w)} × {Math.round(selected.rect.h)} pt
                  {" · arrow keys nudge, shift for 10"}
                </div>

                <button type="button" className="btn-ghost" data-testid="ff-delete"
                        style={{ color: "var(--secondary)", fontSize: 13, padding: "6px 0" }}
                        onClick={() => {
                          setFields((xs) => xs.filter((f) => f.field_id !== selected.field_id));
                          setSel("");
                          setDirty(true);
                        }}>
                  <Trash2 size={13} /> Remove this field
                </button>
              </div>
            )}
          </div>

          {/* ---------------------------------------------------- the page */}
          {/* Deselect on pointerdown, not mousedown: the boxes drag with pointer
              events, and stopping a pointerdown does nothing to the mousedown
              that follows it -- so a mousedown handler here would wipe the
              selection the drag had just made, one frame after making it. */}
          <div style={{ flex: 1, overflow: "auto", padding: 20, minWidth: 0 }}
               onPointerDown={() => setSel("")}>
            {!meta ? (
              <div style={muted}>Loading…</div>
            ) : !pages ? (
              <div style={{ ...muted, maxWidth: 460 }} data-testid="ff-unmeasured">
                PorchLight couldn't read the pages of this PDF, so there's nothing to place
                fields on. Try re-uploading it, or send it over and we'll take a look.
              </div>
            ) : !pageSize ? (
              <div style={{ ...muted, maxWidth: 460 }} data-testid="ff-no-size">
                PorchLight doesn't have the size of page {page + 1}, so it can't work out
                where a click lands. Re-uploading the form will measure it again.
              </div>
            ) : (
              <div style={{ position: "relative", margin: "0 auto", maxWidth: 820,
                            boxShadow: "0 2px 18px rgba(0,0,0,0.12)", background: "#fff",
                            cursor: armed ? "crosshair" : "default" }}>
                {imgErr ? (
                  <div style={{ padding: 28, color: "var(--secondary)", fontSize: 13.5 }}
                       data-testid="ff-page-error">{imgErr}</div>
                ) : !imgUrl ? (
                  <div style={{ padding: 28, ...muted }}>Rendering page {page + 1}…</div>
                ) : (
                  <>
                    <img ref={imgRef} src={imgUrl} alt={`Page ${page + 1}`} onLoad={measure}
                         data-testid="ff-page-img" draggable={false}
                         onClick={place}
                         style={{ display: "block", width: "100%", height: "auto",
                                  userSelect: "none" }} />

                    {k > 0 && mine.map((f) => {
                      const on = f.field_id === sel;
                      return (
                        <div key={f.field_id} data-testid={`ff-box-${f.field_id}`}
                             onPointerDown={(e) => startDrag(e, f, "move")}
                             onPointerMove={onMove}
                             onPointerUp={endDrag}
                             onClick={(e) => e.stopPropagation()}
                             title={f.label}
                             style={{
                               position: "absolute",
                               left: f.rect.x * k,
                               top: (ph - f.rect.y - f.rect.h) * k,
                               width: f.rect.w * k,
                               height: f.rect.h * k,
                               border: `1.5px ${f.who === "agent" ? "dashed" : "solid"} ${on ? "var(--secondary)" : "var(--primary)"}`,
                               background: on ? "rgba(184,92,71,0.16)" : "rgba(43,76,63,0.12)",
                               cursor: "move", touchAction: "none", boxSizing: "border-box",
                               // While a tool is armed the agent is placing, not
                               // picking: an existing box must not swallow the
                               // click just because the new field overlaps it.
                               pointerEvents: armed ? "none" : "auto",
                             }}>
                          {/* A chip wider than its box overhangs, which is
                              fine above an initials box and silly above a
                              checkbox -- so narrow fields are labelled only
                              while they're the one being worked on. */}
                          {(f.rect.w * k > 28 || on) && (
                            <span style={{ position: "absolute", left: 2, top: "-15px",
                                           fontSize: 9.5, whiteSpace: "nowrap", padding: "0 3px",
                                           borderRadius: 3, color: "#fff",
                                           background: on ? "var(--secondary)" : "var(--primary)" }}>
                              {f.who === "agent" ? "You \u00b7 " : ""}{f.label || KIND[f.kind]?.label}
                            </span>
                          )}
                          <span onPointerDown={(e) => startDrag(e, f, "size")}
                                onPointerMove={onMove}
                                onPointerUp={endDrag}
                                style={{ position: "absolute", right: -4, bottom: -4,
                                         width: 9, height: 9, borderRadius: 2,
                                         background: on ? "var(--secondary)" : "var(--primary)",
                                         cursor: "nwse-resize", touchAction: "none" }} />
                        </div>
                      );
                    })}
                  </>
                )}
              </div>
            )}

            {pages > 0 && (
              <p style={{ ...muted, fontSize: 12, textAlign: "center", marginTop: 14 }}>
                {fields.length} field{fields.length === 1 ? "" : "s"} on this form
                {mine.length !== fields.length ? ` · ${mine.length} on this page` : ""}
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
